import { Children, ReactNode, useMemo, useState } from 'react';

import { PagesContext } from '../../context/PagesContext';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { usePageFocus } from '../../hooks/usePageFocusHook';
import { selectCurrentPage, WizardPageAction } from '../../store/wizard/wizard';
import { setCurrentPage as setCurrentPageStore } from '../../store/wizard/wizard';

type PagesProps = {
    children: ReactNode;
};

export const Pages = ({ children }: PagesProps) => {
    const currentPage = useAppSelector(selectCurrentPage);
    usePageFocus(currentPage);
    const dispatch = useAppDispatch();
    const [pageAction, setPageAction] = useState<WizardPageAction>('next');
    const [sectionTitle, setSectionTitle] = useState<string>('');
    const [numberOfSections, setNumberOfSections] = useState<number>(0);
    const [currentSection, setCurrentSection] = useState<number>(0);

    const activeStepContent = useMemo(() => {
        const reactChildren = Children.toArray(children);

        if (reactChildren.length === 0) {
            console.warn('Make sure to pass your steps as children in your <Pages>');
            return undefined;
        }

        // The index lives in the store, but each state-branched wizard swaps in a different-length page list
        // (see SubmitCoApplicantWizardDK / OngoingApplicationWizardNO). On the render where the branch
        // changes, the index is still the previous branch's, so an unclamped lookup returns `undefined` and
        // paints a blank screen. Clamping renders the nearest valid step instead.
        //
        // Do not rely on the wizards to make this unreachable: they reset the stored index themselves, but
        // with inconsistent timing and coverage -- SubmitCoApplicantWizardDK uses a layout effect (so its
        // stale render is never painted), the other co-applicant/ongoing wizards use a plain effect (one
        // painted frame), and OngoingApplicationWizardSE does not reset at all. The clamp is what guarantees
        // a step is always rendered.
        return reactChildren[Math.min(Math.max(currentPage, 0), reactChildren.length - 1)];
    }, [currentPage, children]);

    const numberOfPages = useMemo(() => Children.toArray(children).length, [children]);

    const setCurrentPage = (currentPage: number) => {
        dispatch(setCurrentPageStore(currentPage));
    };

    return (
        <PagesContext.Provider
            value={{
                currentPage,
                setCurrentPage,
                pageAction,
                setPageAction,
                numberOfPages,
                sectionTitle,
                setSectionTitle,
                currentSection,
                setCurrentSection,
                numberOfSections,
                setNumberOfSections,
            }}>
            {activeStepContent}
        </PagesContext.Provider>
    );
};
